import {
  Directive,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";

/** Context handed to each rendered row (`let-i = index`, or `let-i` for the implicit value). */
export class FillViewportRowsContext {
  constructor(public $implicit: number, public index: number) {}
}

/**
 * Structural directive that repeats its template as many times as it takes to fill the visible
 * height of the surrounding `ion-content`, and keeps the count in sync while that height changes
 * (window resize, rotation, on-screen keyboard).
 *
 * It exists for skeleton placeholders: a hard-coded row count is wrong on every screen but the
 * one it was tuned for — too short on a desktop modal, and past the fold on a phone, where it
 * makes a purely decorative loading state scrollable.
 *
 * The row height cannot be measured before the rows exist (and Ionic rows only get their height
 * once hydrated), so it is passed in and must mirror the height declared in the SCSS, vertical
 * margins included.
 *
 * @example
 * <ion-item class="env-item" *appFillViewportRows="rowHeight; min: 3"></ion-item>
 */
@Directive({
    selector: "[appFillViewportRows]",
    standalone: false
})
export class FillViewportRowsDirective implements OnInit, OnDestroy {
  /** Rows rendered while the available height is still unknown (first paint, no layout yet). */
  private static readonly DEFAULT_MIN_ROWS: number = 3;

  /**
   * Slack left at the bottom, in px. The host bottom edge includes the content padding, so a row
   * that fits exactly would still be a few px past the fold; erring on one row fewer costs an
   * invisible gap, erring on one row more costs the scrollbar this directive exists to remove.
   */
  private static readonly BOTTOM_SLACK: number = 8;

  private _rowHeight: number = 0;
  private _minRows: number = FillViewportRowsDirective.DEFAULT_MIN_ROWS;
  private _resizeObserver: ResizeObserver = null;

  /**
   * Height of a single row in px, vertical margins included. Required: with no usable value the
   * directive falls back to the minimum row count.
   */
  @Input() public set appFillViewportRows(rowHeight: number) {
    this._rowHeight = rowHeight;
    this.render();
  }

  /** Lower bound on the rendered rows. Defaults to 3. */
  @Input() public set appFillViewportRowsMin(minRows: number) {
    this._minRows =
      minRows > 0 ? minRows : FillViewportRowsDirective.DEFAULT_MIN_ROWS;
    this.render();
  }

  constructor(
    private _templateRef: TemplateRef<FillViewportRowsContext>,
    private _viewContainerRef: ViewContainerRef,
    private _ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.observeViewportResize();
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
  }

  /**
   * Watch the scroll host so the row count follows the viewport. The observer also fires once on
   * `observe()`, which is what produces the first correctly sized render: the inputs are applied
   * before the anchor is laid out, so any count computed until now is the fallback.
   */
  private observeViewportResize(): void {
    if (typeof ResizeObserver === "undefined") {
      this.render();
      return;
    }
    const observedElement: Element = this.getScrollHost() || document.body;
    this._resizeObserver = new ResizeObserver(() =>
      this._ngZone.run(() => this.render())
    );
    this._resizeObserver.observe(observedElement);
  }

  /** Add or remove rows until the rendered count matches the space available. */
  private render(): void {
    const targetRows: number = this.getTargetRows();
    if (targetRows === this._viewContainerRef.length) {
      return;
    }
    while (this._viewContainerRef.length > targetRows) {
      this._viewContainerRef.remove(this._viewContainerRef.length - 1);
    }
    while (this._viewContainerRef.length < targetRows) {
      const index: number = this._viewContainerRef.length;
      this._viewContainerRef.createEmbeddedView(
        this._templateRef,
        new FillViewportRowsContext(index, index)
      );
    }
  }

  /**
   * @returns how many rows fit in the space left below the anchor, never fewer than the minimum
   */
  private getTargetRows(): number {
    if (this._rowHeight <= 0) {
      return this._minRows;
    }
    const availableHeight: number = this.getAvailableHeight();
    return Math.max(this._minRows, Math.floor(availableHeight / this._rowHeight));
  }

  /**
   * @returns the height in px between the top of the rows and the bottom of the scroll host, or 0
   *          when nothing is laid out yet
   */
  private getAvailableHeight(): number {
    const anchorParent: HTMLElement = this.getAnchorParent();
    if (!anchorParent) {
      return 0;
    }
    const scrollHost: HTMLElement = this.getScrollHost();
    const hostRect: DOMRect = scrollHost?.getBoundingClientRect();
    const hostTop: number = hostRect ? hostRect.top : 0;
    const hostBottom: number = hostRect ? hostRect.bottom : window.innerHeight;
    /* Clamp to the host top: once the rows overflow and the host is scrolled, the anchor climbs
     * out of view, and an unclamped measure would keep asking for more rows. */
    const rowsTop: number = Math.max(
      anchorParent.getBoundingClientRect().top,
      hostTop
    );
    return hostBottom - rowsTop - FillViewportRowsDirective.BOTTOM_SLACK;
  }

  /** @returns the element the rows are rendered into (the anchor comment node's parent) */
  private getAnchorParent(): HTMLElement {
    const anchorNode: Node = this._viewContainerRef.element.nativeElement;
    return anchorNode?.parentElement;
  }

  /** @returns the `ion-content` the rows scroll in, or null outside of one */
  private getScrollHost(): HTMLElement {
    return this.getAnchorParent()?.closest("ion-content") as HTMLElement;
  }
}
